// Programmatic sound effects using the Web Audio API

let ctx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!ctx || ctx.state === 'closed') {
    ctx = new AudioContext();
  }
  return ctx;
}

function playTone(
  frequency: number,
  duration: number,
  volume: number,
  type: OscillatorType = 'sine',
  fadeOut = true
): void {
  try {
    const ac = getCtx();
    const osc = ac.createOscillator();
    const gain = ac.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ac.currentTime);

    gain.gain.setValueAtTime(volume, ac.currentTime);
    if (fadeOut) {
      gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + duration);
    }

    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(ac.currentTime);
    osc.stop(ac.currentTime + duration);
  } catch {
    // Audio not available (e.g. no user gesture yet) — silently skip
  }
}

/** Two-note rising chime — played when YOU join a voice channel */
export function playJoinSelf(): void {
  playTone(880, 0.12, 0.25, 'sine');
  setTimeout(() => playTone(1320, 0.18, 0.2, 'sine'), 120);
}

/** Single soft blip — played when ANOTHER user joins */
export function playJoinOther(): void {
  playTone(660, 0.15, 0.15, 'sine');
}

/** Single low blip — played when a user leaves */
export function playLeave(): void {
  playTone(440, 0.15, 0.15, 'sine');
}

/** Short click-down — played when muting */
export function playMute(): void {
  playTone(300, 0.08, 0.2, 'square', false);
  setTimeout(() => playTone(220, 0.08, 0.15, 'square'), 80);
}

/** Short click-up — played when unmuting */
export function playUnmute(): void {
  playTone(220, 0.08, 0.15, 'square', false);
  setTimeout(() => playTone(300, 0.08, 0.2, 'square'), 80);
}

// ── Ringtone & Dialtone (looping, stoppable) ─────────────────────────────────

let ringtoneAudio: HTMLAudioElement | null = null;
let dialtoneAudio: HTMLAudioElement | null = null;
let dialtoneInterval: ReturnType<typeof setInterval> | null = null;

function resolveAssetUrl(fileName: string): string {
  try {
    // Works for both dev (http://localhost:*) and packaged Electron (file://.../dist/index.html)
    return new URL(fileName, window.location.href).href;
  } catch {
    // Fallback for edge cases
    return `/${fileName}`;
  }
}

/**
 * Play the custom .ogg ringtone, looping until stopRingtone() is called.
 */
export function playRingtone(): void {
  stopRingtone();
  try {
    ringtoneAudio = new Audio(resolveAssetUrl('ringtone.ogg'));
    ringtoneAudio.loop = true;
    ringtoneAudio.volume = 0.6;
    ringtoneAudio.play().catch(() => {});
  } catch { /* ignore */ }
}

export function stopRingtone(): void {
  if (ringtoneAudio) {
    ringtoneAudio.pause();
    ringtoneAudio.currentTime = 0;
    ringtoneAudio = null;
  }
}

/**
 * Play calling.ogg in loop while waiting for the other party to answer.
 */
export function playDialtone(): void {
  stopDialtone();
  try {
    dialtoneAudio = new Audio(resolveAssetUrl('calling.ogg'));
    dialtoneAudio.loop = true;
    dialtoneAudio.volume = 0.6;
    dialtoneAudio.play().catch(() => {});
  } catch { /* ignore */ }
}

export function stopDialtone(): void {
  if (dialtoneAudio) {
    dialtoneAudio.pause();
    dialtoneAudio.currentTime = 0;
    dialtoneAudio = null;
  }
  if (dialtoneInterval) {
    clearInterval(dialtoneInterval);
    dialtoneInterval = null;
  }
}
